import useMercureContext from "@lib/hooks/useMercureContext";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

interface MercureEvent {
  type?: string;
  payload?: any;
  "@id"?: string;
  topic?: string;
}

type UpdateStrategy =
  | "invalidate"
  | "optimistic"
  | "collection"
  | ((data: any, queryClient: any) => void);

interface UseMercureOptions {
  enabled?: boolean;
  onMessage?: (data: MercureEvent) => void;
  queryKey?: any[];
  onUpdate?: UpdateStrategy;
  format?: "json" | "api-platform";
  resourceName?: string;
  getId?: (item: any) => string | number;
  onError?: (error: Error) => void;
}

function parseApiPlatformUpdate(rawUpdate: any): MercureEvent {
  if (typeof rawUpdate === "string") {
    try {
      return JSON.parse(rawUpdate);
    } catch {
      return rawUpdate as unknown as MercureEvent;
    }
  }
  return rawUpdate as MercureEvent;
}

export function useMercure(topic: string, options: UseMercureOptions = {}) {
  const {
    enabled = true,
    onMessage,
    queryKey,
    onUpdate,
    format = "json",
    resourceName,
    getId = (item: any) => item.id,
    onError,
  } = options;

  const mercureClient = useMercureContext();
  const queryClient = useQueryClient();

  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const resourceNameRef = useRef(resourceName);
  useEffect(() => {
    resourceNameRef.current = resourceName;
  }, [resourceName]);

  const getIdRef = useRef(getId);
  useEffect(() => {
    getIdRef.current = getId;
  }, [getId]);

  useEffect(() => {
    if (!mercureClient || !topic || !enabled) {
      return;
    }

    const handleMessage = (rawUpdate: any) => {
      try {
        const data = format === "api-platform" ? parseApiPlatformUpdate(rawUpdate) : rawUpdate;

        if (onMessageRef.current) {
          onMessageRef.current(data);
        }

        if (queryKey && onUpdateRef.current) {
          const updateStrategy = onUpdateRef.current;

          if (updateStrategy === "invalidate") {
            queryClient.invalidateQueries({ queryKey });
          } else if (updateStrategy === "optimistic") {
            queryClient.setQueryData(queryKey, (oldData: any) => ({
              ...oldData,
              ...data,
            }));
          } else if (updateStrategy === "collection") {
            const currentCollection = queryClient.getQueryData(queryKey) || [];
            const eventType = data.type?.toLowerCase() || "";
            const resource = resourceNameRef.current;

            if (!resource) {
              console.warn('useMercure: resourceName is required when using "collection" strategy');
              return;
            }

            let updatedCollection: any[] | undefined;
            const payload = data.payload || data;
            const collection = currentCollection as any[];

            if (eventType === `${resource}.created` || eventType.includes("created")) {
              updatedCollection = [...collection, payload];
            } else if (eventType === `${resource}.updated` || eventType.includes("updated")) {
              updatedCollection = collection.map((item: any) =>
                getIdRef.current(item) === getIdRef.current(payload) ? payload : item,
              );
            } else if (eventType === `${resource}.deleted` || eventType.includes("deleted")) {
              updatedCollection = collection.filter(
                (item: any) => getIdRef.current(item) !== getIdRef.current(payload),
              );
            }

            if (updatedCollection) {
              queryClient.setQueryData(queryKey, updatedCollection);
              queryClient.invalidateQueries({
                queryKey,
                refetchType: "none",
              });
            }
          } else if (typeof updateStrategy === "function") {
            updateStrategy(data, queryClient);
          }
        }
      } catch (error) {
        console.error("Error processing Mercure event:", error);
        onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    };

    const unsubscribe = (mercureClient as any).subscribe(topic, handleMessage);

    return () => {
      unsubscribe();
    };
  }, [mercureClient, topic, enabled, queryClient, queryKey, format, onError]);
}
